
import { getAreas, getCities, getWarehouses } from "../../../shared/api/Nova-poshta";
import { getMeestAreas, getMeestBranches, getMeestCities } from "../../../shared/api/Meest";
import { useEffect, useRef, useState } from "react";

import { DELIVERY_TYPES } from "../const/delivery";

export const useDeliveryLogic = (values, setFieldValue, t) => {
    const [areas, setAreas] = useState([]);
    const [cities, setCities] = useState([]);
    const [warehouses, setWarehouses] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [apiError, setApiError] = useState(null);

    // Додаємо рефи, щоб запам'ятати попередні значення.
    // При першому рендері сюди запишуться дані з бази юзера.
    const prevAreaRef = useRef(values.area);
    const prevCityRef = useRef(values.city);
    const prevDeliveryTypeRef = useRef(values.deliveryType);

    const isMeestDelivery = values.deliveryType === DELIVERY_TYPES.MEEST_BRANCH;

    const shouldLoadDeliveryData = () => {
        return Object.values(DELIVERY_TYPES).includes(values.deliveryType);
    };

    // 1. Загрузка Областей
    useEffect(() => {
        if (!shouldLoadDeliveryData()) {
            setAreas([]); setCities([]); setWarehouses([]); setApiError(null);
            return;
        }

        setIsLoading(true); setApiError(null);

        if (isMeestDelivery) {
            getMeestAreas("UA")
                .then(setAreas)
                .catch(() => setApiError(t('delivery.meestAreasErrorMessage')))
                .finally(() => setIsLoading(false));
            return;
        }

        getAreas()
            .then(setAreas)
            .catch(() => setApiError(t('delivery.novaPoshtaAreasErrorMessage')))
            .finally(() => setIsLoading(false));
    }, [values.deliveryType, t]);

    // 2. Загрузка Городов
    useEffect(() => {
        if (!values.area || !shouldLoadDeliveryData()) return;

        // Перевіряємо, чи область або тип доставки РЕАЛЬНО змінилися
        const isAreaChanged = prevAreaRef.current !== values.area;
        const isDeliveryTypeChanged = prevDeliveryTypeRef.current !== values.deliveryType;

        // Скидаємо поля ТІЛЬКИ якщо це дія користувача, а не перше завантаження
        if (isAreaChanged || isDeliveryTypeChanged) {
            setFieldValue("city", "");
            setFieldValue("warehouse", "");
            setCities([]);
            setWarehouses([]);
        }

        // Оновлюємо "пам'ять" рефів поточними значеннями
        prevAreaRef.current = values.area;
        prevDeliveryTypeRef.current = values.deliveryType;

        setApiError(null); setIsLoading(true);

        if (isMeestDelivery) {
            getMeestCities(values.area)
                .then(setCities)
                .catch(() => setApiError(t('delivery.meestCitiesErrorMessage')))
                .finally(() => setIsLoading(false));
            return;
        }

        getCities(values.area)
            .then(setCities)
            .catch(() => setApiError(t('delivery.novaPoshtaCitiesErrorMessage')))
            .finally(() => setIsLoading(false));
    }, [values.area, values.deliveryType, setFieldValue, t]);

    // 3. Загрузка Отделений
    useEffect(() => {
        if (!values.city || !shouldLoadDeliveryData()) return;

        // Перевіряємо, чи місто РЕАЛЬНО змінилося
        const isCityChanged = prevCityRef.current !== values.city;
        const isDeliveryTypeChanged = prevDeliveryTypeRef.current !== values.deliveryType;

        if (isCityChanged || isDeliveryTypeChanged) {
            setFieldValue("warehouse", "");
            setWarehouses([]);
        }

        prevCityRef.current = values.city;

        setApiError(null); setIsLoading(true);

        if (isMeestDelivery) {
            getMeestBranches(values.city)
                .then(setWarehouses)
                .catch(() => setApiError(t('delivery.meestWarehousesErrorMessage')))
                .finally(() => setIsLoading(false));
            return;
        }

        getWarehouses(values.city, "branch")
            .then(setWarehouses)
            .catch(() => setApiError(t('delivery.novaPoshtaWarehousesErrorMessage')))
            .finally(() => setIsLoading(false));
    }, [values.city, values.deliveryType, setFieldValue, t]);

    // Форматирование для AutoCompleteSelect
    const formatOptions = (data) => data?.map(item => ({ value: item.Ref, label: item.Description })) || [];

    return {
        isLoading,
        apiError,
        shouldLoadDeliveryData,
        areaOptions: formatOptions(areas),
        cityOptions: formatOptions(cities),
        warehouseOptions: formatOptions(warehouses),
    };
};